import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { paymentsApiService, PaymentCreateDTO, PaymentUpdateDTO, PaymentFilters } from '../api/payments-api';
import { Payment } from '../api/payments-api';

// Estado inicial
interface PaymentsState {
  payments: Payment[];
  currentPayment: Payment | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: PaymentFilters;
}

const initialState: PaymentsState = {
  payments: [],
  currentPayment: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchPayments = createAsyncThunk(
  'payments/fetchPayments',
  async (filters: PaymentFilters = {}, { rejectWithValue }) => {
    try {
      const response = await paymentsApiService.getPayments(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar pagos'
      );
    }
  }
);

export const fetchPayment = createAsyncThunk(
  'payments/fetchPayment',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await paymentsApiService.getPaymentById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar pago'
      );
    }
  }
);

export const fetchPaymentsByInvoice = createAsyncThunk(
  'payments/fetchPaymentsByInvoice',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await paymentsApiService.getPaymentsByInvoice(invoiceId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar pagos de la factura'
      );
    }
  }
);

export const createPayment = createAsyncThunk(
  'payments/createPayment',
  async (data: PaymentCreateDTO, { rejectWithValue }) => {
    try {
      const response = await paymentsApiService.createPayment(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear pago'
      );
    }
  }
);

export const updatePayment = createAsyncThunk(
  'payments/updatePayment',
  async (
    { id, data }: { id: string; data: PaymentUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentsApiService.updatePayment(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar pago'
      );
    }
  }
);

export const deletePayment = createAsyncThunk(
  'payments/deletePayment',
  async (id: string, { rejectWithValue }) => {
    try {
      await paymentsApiService.deletePayment(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar pago'
      );
    }
  }
);

// Slice
const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<PaymentFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentPayment: (state, action: PayloadAction<Payment | null>) => {
      state.currentPayment = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: builder => {
    // Fetch payments
    builder
      .addCase(fetchPayments.pending, state => {
        if (state.payments.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.payments = payload.payments || [];
        
        const newPage = payload.page || 1;
        const newLimit = payload.limit || 10;
        const newTotal = payload.total || 0;
        const newTotalPages = payload.totalPages || 1;
        
        if (
          state.pagination.page !== newPage ||
          state.pagination.limit !== newLimit ||
          state.pagination.total !== newTotal ||
          state.pagination.totalPages !== newTotalPages
        ) {
          state.pagination = {
            page: newPage,
            limit: newLimit,
            total: newTotal,
            totalPages: newTotalPages,
          };
        }
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch payment
    builder
      .addCase(fetchPayment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPayment = action.payload as Payment;
      })
      .addCase(fetchPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch payments by invoice
    builder
      .addCase(fetchPaymentsByInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload as Payment[] || [];
      })
      .addCase(fetchPaymentsByInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create payment
    builder
      .addCase(createPayment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.loading = false;
        const newPayment = action.payload as Payment;
        state.payments.unshift(newPayment);
        state.pagination.total += 1;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update payment
    builder
      .addCase(updatePayment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPayment = action.payload as Payment;
        const index = state.payments.findIndex(
          payment => payment.id === updatedPayment.id
        );
        if (index !== -1) {
          state.payments[index] = updatedPayment;
        }
        if (state.currentPayment?.id === updatedPayment.id) {
          state.currentPayment = updatedPayment;
        }
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete payment
    builder
      .addCase(deletePayment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = state.payments.filter(
          payment => payment.id !== action.payload
        );
        state.pagination.total -= 1;
        if (state.currentPayment?.id === action.payload) {
          state.currentPayment = null;
        }
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentPayment,
  clearError,
  setPagination,
} = paymentsSlice.actions;

// Selectors
export const selectPayments = (state: { payments: PaymentsState }) => state.payments.payments;
export const selectCurrentPayment = (state: { payments: PaymentsState }) => state.payments.currentPayment;
export const selectPaymentsLoading = (state: { payments: PaymentsState }) => state.payments.loading;
export const selectPaymentsError = (state: { payments: PaymentsState }) => state.payments.error;
export const selectPaymentsPagination = (state: { payments: PaymentsState }) => state.payments.pagination;
export const selectPaymentsFilters = (state: { payments: PaymentsState }) => state.payments.filters;

export default paymentsSlice.reducer;
